import { useCallback, useId, useRef, useState } from 'react';
import { ModalDialog } from '../../job-roles/components/ModalDialog';
import { LocationEditor } from '../../business-profile-setup/work-locations/LocationEditor';
import type { WorkLocationResponse } from '../../business-profile-setup/types';
import '../../business-profile-setup/business-profile-setup.css';

export interface AddWorkLocationDialogProps {
  onClose: () => void;
  /** The server has created the location. The opener refreshes its list and selects it. */
  onAdded: (created: WorkLocationResponse) => void;
}

/**
 * EPN-233: add a work location without leaving Add Employee or the employee profile. It is Step 5's own "Add New Location" form (two tabs:
 * Branch Details, Payroll Tax Registration) in a prompt, so the fields, validation, registration-incomplete warning and masked account
 * numbers behave exactly as they do under Company Details. Nothing is saved until the server confirms; Cancel and Escape with unsaved
 * entries ask before discarding them. The opener underneath stays mounted, so whatever the admin typed there is kept.
 */
export function AddWorkLocationDialog({ onClose, onAdded }: AddWorkLocationDialogProps) {
  const uid = useId();
  const [saving, setSaving] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const dirtyRef = useRef(false);

  const onDirtyChange = useCallback((value: boolean) => {
    dirtyRef.current = value;
  }, []);

  const requestClose = useCallback(() => {
    if (dirtyRef.current) setConfirmDiscard(true);
    else onClose();
  }, [onClose]);

  return (
    <>
      <ModalDialog title="Add New Work Location" size="xl" busy={saving || confirmDiscard} onClose={requestClose} describedBy={`${uid}-intro`}>
        <div className="bps-shell bps-shell--prompt">
          <p className="muted" id={`${uid}-intro`}>
            It is saved for your company, the same as adding it under Company Details, and will be available for every employee. Everything you have entered so far is kept.
          </p>
          <LocationEditor
            existing={null}
            mode="dialog"
            onReload={() => undefined}
            onDirtyChange={onDirtyChange}
            onSavingChange={setSaving}
            onCancel={requestClose}
            onSaved={onAdded}
          />
        </div>
      </ModalDialog>
      {confirmDiscard && (
        <ModalDialog
          title="Discard Changes?"
          size="sm"
          role="alertdialog"
          describedBy={`${uid}-discard`}
          onClose={() => setConfirmDiscard(false)}
          footer={(
            <>
              <button type="button" className="jr-btn jr-btn--secondary" data-autofocus onClick={() => setConfirmDiscard(false)}>Keep Editing</button>
              <button type="button" className="jr-btn jr-btn--primary" onClick={onClose}>Discard Changes</button>
            </>
          )}
        >
          <p id={`${uid}-discard`}>You have unsaved information. If you close now, this work location will not be added.</p>
        </ModalDialog>
      )}
    </>
  );
}
